import { useState, useEffect, useCallback, useRef } from "react";
import { useAppSelector } from "../redux/hooks";
import { selectHasPendingFileChanges } from "../redux/slices/auditReportSlice";
import { errorMessage } from "../utils/mutationResult";

interface UseDebouncedSaveOptions {
  delay?: number;
  onSave: (data: any) => Promise<void>;
  onError?: (error: Error) => void;
  readOnly?: boolean;
}

export function useDebouncedSave<T>(
  data: T,
  { delay = 2000, onSave, onError, readOnly }: UseDebouncedSaveOptions,
) {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const hasPendingFileChanges = useAppSelector(selectHasPendingFileChanges);

  // Check Redux for viewing historical version (read-only mode)
  const isViewingHistoricalVersion = readOnly;

  const timeoutRef = useRef<NodeJS.Timeout>(undefined);
  const previousDataRef = useRef<T>(data);
  const pendingBaselineRef = useRef<T | null>(null);
  // Hydration callers explicitly establish their loaded baseline with resetBaseline.
  // Start settled so the first user edit is never consumed as hydration noise.
  const hasSettledInitialDataRef = useRef(true);
  const skipNextChangeRef = useRef(false);
  const saveCountRef = useRef(0);

  // Track changes
  useEffect(() => {
    if (pendingBaselineRef.current !== null) {
      const baseline = pendingBaselineRef.current;

      // Ignore the stale render whose effect may run after a hydration reset.
      if (JSON.stringify(data) === JSON.stringify(baseline)) {
        previousDataRef.current = data;
        pendingBaselineRef.current = null;
      }
      return;
    }

    const hasChanged =
      JSON.stringify(data) !== JSON.stringify(previousDataRef.current);
    if (hasChanged) {
      // Form components hydrate their local state after the first render. Treat
      // that first post-mount replacement as the loaded baseline, not as a user
      // edit, so refreshing a section does not enable Save by itself.
      if (!hasSettledInitialDataRef.current) {
        hasSettledInitialDataRef.current = true;
        previousDataRef.current = data;
        return;
      }
      if (skipNextChangeRef.current) {
        skipNextChangeRef.current = false;
        previousDataRef.current = data;
        return;
      }
      setHasUnsavedChanges(true);
      setSaveError(null);
      previousDataRef.current = data;
    }
  }, [data]);

  // Debounced auto-save (disabled if viewing historical version)

  const performSave = useCallback(async () => {
    if (
      isSaving ||
      isViewingHistoricalVersion ||
      (!hasUnsavedChanges && !hasPendingFileChanges)
    )
      return;

    setIsSaving(true);
    setSaveError(null);
    skipNextChangeRef.current = false;
    const saveId = ++saveCountRef.current;

    try {
      await onSave(data);

      // Only update state if this is still the latest save operation
      if (saveId === saveCountRef.current) {
        if (JSON.stringify(data) === JSON.stringify(previousDataRef.current)) {
          skipNextChangeRef.current = false;
        }
        setLastSaved(new Date());
        setHasUnsavedChanges(
          JSON.stringify(data) !== JSON.stringify(previousDataRef.current),
        );
      }
    } catch (error) {
      skipNextChangeRef.current = false;
      if (saveId === saveCountRef.current) {
        const message = errorMessage(
          error,
          "Save failed. Your changes are still here. Please retry.",
        );
        setSaveError(message);
        if (onError) {
          onError(error instanceof Error ? error : new Error(message));
        }
      }
    } finally {
      if (saveId === saveCountRef.current) {
        setIsSaving(false);
      }
    }
  }, [
    data,
    onSave,
    onError,
    isSaving,
    isViewingHistoricalVersion,
    hasUnsavedChanges,
    hasPendingFileChanges,
  ]);

  // Debounced auto-save (disabled if viewing historical version)
  useEffect(() => {
    if (
      hasUnsavedChanges &&
      !saveError &&
      !isSaving &&
      !isViewingHistoricalVersion
    ) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        performSave();
      }, delay);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [
    data,
    hasUnsavedChanges,
    saveError,
    isSaving,
    delay,
    isViewingHistoricalVersion,
    performSave,
  ]);

  const forceSave = useCallback(() => {
    if (
      (!hasUnsavedChanges && !hasPendingFileChanges) ||
      isSaving ||
      isViewingHistoricalVersion
    )
      return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    performSave();
  }, [
    performSave,
    hasUnsavedChanges,
    hasPendingFileChanges,
    isSaving,
    isViewingHistoricalVersion,
  ]);

  const resetBaseline = useCallback((nextData: T) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    previousDataRef.current = nextData;
    pendingBaselineRef.current = nextData;
    // The baseline is now explicitly known (loaded data), so any future change
    // is a real user edit, not the initial hydration settle this ref guards.
    hasSettledInitialDataRef.current = true;
    setHasUnsavedChanges(false);
    setSaveError(null);
  }, []);

  return {
    isSaving,
    lastSaved,
    hasUnsavedChanges: hasUnsavedChanges || hasPendingFileChanges,
    saveError,
    forceSave,
    resetBaseline,
  };
}
